import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "components/common/button/Button";
import DropdownInput from "components/common/form/inputs/dropdown/DropdownInput";
import FormActions from "components/common/form/partials/FormActions";
import If from "components/common/If";
import Translate from "components/common/Translate";
import ModalPortal from "components/modal/ModalPortal";
import { uuid } from "config/app-constants";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, useLocation, useSearchParams } from "react-router-dom";
import { DatalistTableColumnType } from "types/datalist-types";
import { DropdownInputPropsType } from "types/form-types";
import { SortEnumType } from "types/graphql";
import { getTranslationText } from "utilities/translation-utilities";
import useAppContext from "../../../../../utilities/hooks/useAppContext";
import StringUtilities from "../../../../../utilities/common-utilities/string-utilities";

export type DatalistSortType = {
    name?: string;
    direction?: SortEnumType;
    id: string;
}

export type DatalistSortPropsType<T, TSort> = {
    sort?: DatalistSortType[];
    columns: DatalistTableColumnType<T, any, TSort>[],
    name: string,
    triggerChange: () => void,
}

export const SORT_QUERY_PARAM = 'sort';

export const SORT_MODAL_ID = 'datalist-sort';

const DatalistSort = <T, TSort>(props: DatalistSortPropsType<T, TSort>) => {
    const [t] = useTranslation();
    const [disableFormActions, setDisableFormActions] = useState(false);
    const location = useLocation();
    const context = useAppContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const [model, setModel] = useState<DatalistSortType[]>(props.sort ? [...props.sort] : [{
        id: uuid(),
    }]);

    let sort_query_param = `${props.name}-${SORT_QUERY_PARAM}`;
    let sort_modal_id = `${props.name}-${SORT_MODAL_ID}`;

    useEffect(() => {
        const sort = context.searchParams.get(sort_query_param);
        if (sort) {
            setModel(sort);
        } else {
            setModel(props.sort ? [...props.sort] : [{
                id: uuid(),
            }]);
        }
    }, [location]);

    const fieldDropdownConfig: DropdownInputPropsType<DatalistTableColumnType<T, any, TSort>, any> = {
        dataSource: {
            data: props.columns,
            search: 'off'
        },
        getItemId(i) {
            return i.name;
        },
        view: {
            inputView(i?) {
                if (!Array.isArray(i?.sort) && typeof i?.sort === 'object' && i.sort.title) {
                    return getTranslationText(i.sort.title, t);
                }

                return typeof i?.title === 'string' ? getTranslationText(i.title, t) : (t(`fields.${i?.name}.shortName`) || '');
            }
        },
        placeholder: t('data-list.sort-by')
    }

    const directionDropdownConfig: DropdownInputPropsType<any, SortEnumType> = {
        dataSource: {
            data: [
                SortEnumType.Asc,
                SortEnumType.Desc
            ],
            search: 'off'
        },
        getItemId: i => i,
        view: {
            inputView: i => i
        },
        placeholder: t('data-list.sort-dir')
    }

    const changeValue = (i: number, val: {
        column?: DatalistTableColumnType<T, any, TSort>,
        direction: SortEnumType
    }) => {
        const newModel = [...model];
        newModel[i] = {
            direction: val.direction,
            name: val.column?.name || model[i].name,
            id: model[i].id
        };
        setModel(newModel);
    }

    const remove = (id: string) => {
        setModel(model.filter(c => c.id !== id));
    }

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setDisableFormActions(true);
        setQueryParams(model);
        setDisableFormActions(false);
    }

    const onReset = () => {
        setQueryParams();
    }

    const setQueryParams = (sort?: DatalistSortType[]) => {
        searchParams.delete(sort_query_param);
        searchParams.delete(sort_modal_id);
        if (sort) searchParams.append(sort_query_param, StringUtilities.stringifyValue(sort));
        setSearchParams(searchParams, {
            replace: true
        });
        setTimeout(() => {
            props.triggerChange();
        }, 50);
    }

    if (props.columns.length === 0) {
        return <></>;
    }
    return (
        <ModalPortal
            title={t('data-list.sorters')}
            name={sort_modal_id}
        >
            <div className="pb-20 md:pb-0">
                <Form onSubmit={onSubmit} onReset={onReset} name={`${props.name}-datalist-sort-form`}>
                    <div className="p-4">
                        <ul>
                            {model.map((c, i) => {
                                return (
                                    <li key={'datalist-sort-' + c.id} className="flex gap-2">
                                        <DropdownInput
                                            {...fieldDropdownConfig}
                                            value={props.columns.find(col => col.name === c.name)}
                                            name="column"
                                            onChange={v => changeValue(i, { column: v, direction: SortEnumType.Asc })}
                                            required
                                        />
                                        <DropdownInput
                                            {...directionDropdownConfig}
                                            value={c.direction}
                                            onChange={v => changeValue(i, { direction: v })}
                                            name="direction"
                                            required
                                        />
                                        <Button theme="link" title={t('button.remove')} className="text-danger"
                                            onClick={() => remove(c.id)}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </Button>
                                    </li>
                                )
                            })}
                        </ul>
                        <If condition={model.length < props.columns.length}>
                            <Button theme="link" title="add" onClick={() => setModel([...model, ...[{ id: uuid() }]])}>
                                <FontAwesomeIcon icon={faPlus} />
                                <span> <Translate i18nKey="button.add" /></span>
                            </Button>
                        </If>
                    </div>
                    <FormActions disableInputs={disableFormActions} />
                </Form>
            </div>
        </ModalPortal>
    );
};

export default DatalistSort;
